import { useStudioSettings } from "../../stores/studio";
import type { Layer } from "./scene";
import { activeScene } from "./scene";

/**
 * Everything on the frame, front to back.
 *
 * ## Why every layer is here and not just the images
 *
 * Reordering used to live in the image list, so an image could be moved through
 * the stack and nothing else could. Adding a background then covered the
 * camera, the now-playing card and the host's name, with no way to push it
 * behind them - the one thing anybody adding a background wants to do.
 *
 * A layer is a layer. The camera is not a different kind of thing from a
 * picture the host uploaded, and the moment the compositor stopped having named
 * slots this list should have stopped having a special case.
 *
 * ## Front at the top
 *
 * The scene paints back to front, so the array's last entry is the frontmost.
 * This shows the reverse, because every stacking control anybody has used puts
 * the front at the top - and "up" meaning "further back" is the kind of thing
 * people work around rather than report.
 */
export function LayerList({
  selected,
  onSelect,
}: {
  selected: string | null;
  onSelect: (id: string | null) => void;
}) {
  const book = useStudioSettings((s) => s.book);
  const updateLayer = useStudioSettings((s) => s.updateLayer);
  const moveLayer = useStudioSettings((s) => s.moveLayer);
  const removeLayer = useStudioSettings((s) => s.removeLayer);

  const scene = activeScene(book);
  const front = [...scene.layers].reverse();

  if (front.length === 0) {
    return (
      <p className="font-mono text-[10px] leading-relaxed text-ghost">
        nothing on the frame, pick a layout above, or add an image
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1" data-studio-layers>
      {front.map((layer, i) => (
        <li
          key={layer.id}
          data-studio-layer={layer.id}
          // By kind as well as by id: a test wanting "the now-playing card"
          // knows what it is, not which id it happens to have this run.
          data-studio-layer-kind={layer.kind}
          data-selected={layer.id === selected || undefined}
          className={`flex items-center gap-1 rounded-[var(--radius-control)] border px-2 py-1.5 transition-colors ${
            layer.id === selected ? "border-accent" : "border-line"
          }`}
        >
          <button
            type="button"
            onClick={() => onSelect(layer.id)}
            /*
             * Named for what pressing it does, not just for the layer.
             *
             * The glyph beside it is `aria-hidden`, so without this the button's
             * accessible name was bare "Camera" - identical to the Camera
             * preset in the picker directly above, which made "the Camera
             * button" ambiguous to anything choosing by name, screen readers
             * included.
             */
            aria-label={`Select the ${NAMES[layer.kind].toLowerCase()} layer`}
            data-studio-layer-select
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
          >
            {layer.kind === "image" ? (
              <img
                src={layer.src}
                alt=""
                className="h-6 w-6 shrink-0 rounded object-contain"
              />
            ) : (
              <span
                aria-hidden
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-raised font-mono text-[9px] text-faint"
              >
                {GLYPHS[layer.kind]}
              </span>
            )}

            <span
              className={`min-w-0 flex-1 truncate text-[12px] ${
                layer.visible ? "text-soft" : "text-ghost line-through"
              }`}
            >
              {NAMES[layer.kind]}
            </span>
          </button>

          {/*
            Disabled at the ends rather than hidden, so the row does not change
            width as a layer moves through the stack and the buttons stay under
            the pointer for a second press.

            `i` counts from the front, so "forward" is a step *later* in the
            painting order - which is `+1` in the array this reverses.
          */}
          <Step
            label="Bring forward"
            glyph="↑"
            hook="forward"
            disabled={i === 0}
            onClick={() => moveLayer(layer.id, 1)}
          />
          <Step
            label="Send backward"
            glyph="↓"
            hook="back"
            disabled={i === front.length - 1}
            onClick={() => moveLayer(layer.id, -1)}
          />

          <button
            type="button"
            onClick={() => updateLayer(layer.id, { visible: !layer.visible })}
            aria-pressed={layer.visible}
            aria-label={layer.visible ? "Hide" : "Show"}
            data-studio-layer-visible
            className={`shrink-0 rounded px-1 py-1 font-mono text-[10px] transition-colors ${
              layer.visible ? "text-soft hover:text-ink" : "text-ghost hover:text-soft"
            }`}
          >
            {layer.visible ? "shown" : "hidden"}
          </button>

          {/*
            Only images can be removed.

            The camera, the card and the name badge come from the layout, and
            deleting one would leave a scene the layout picker could not put
            back without discarding everything else. Hiding does what somebody
            reaching for delete actually wants, and is reversible.
          */}
          {layer.kind === "image" && (
            <button
              type="button"
              onClick={() => {
                removeLayer(layer.id);

                if (layer.id === selected) onSelect(null);
              }}
              aria-label="Remove from the frame"
              data-studio-layer-remove
              className="shrink-0 rounded px-1 py-1 font-mono text-[10px] text-soft transition-colors hover:text-hot-text"
            >
              ×
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function Step({
  label,
  glyph,
  hook,
  disabled,
  onClick,
}: {
  label: string;
  glyph: string;
  hook: string;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-studio-layer-move={hook}
      className="shrink-0 rounded px-1 py-1 font-mono text-[10px] text-soft transition-colors hover:text-ink disabled:opacity-25 disabled:hover:text-soft"
    >
      {glyph}
    </button>
  );
}

const NAMES: Record<Layer["kind"], string> = {
  camera: "Camera",
  card: "Now playing",
  brand: "Your name",
  image: "Image",
};

/** A small mark per kind, so the list scans without reading every row. */
const GLYPHS: Record<Layer["kind"], string> = {
  camera: "cam",
  card: "trk",
  brand: "you",
  image: "img",
};
